import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Button, Chip, CircularProgress, Container, Divider, Stack, TextField, Typography } from "@mui/material";
import { FaLocationDot, FaCalendarDays, FaClock } from "react-icons/fa6";
import { MdOutlineHandyman, MdDescription } from "react-icons/md";
import { toast } from "react-toastify";
import { apiHandle } from "../../config/apiHandle/apiHandle";
import { themeOrange } from "../../utils/colorTheme";

const QuoteRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.userAuth);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quoteRequest, setQuoteRequest] = useState(null);
  const [alreadyQuoted, setAlreadyQuoted] = useState(false);
  const [maxReached, setMaxReached] = useState(false);
  const [quoteCount, setQuoteCount] = useState(0);

  const [price, setPrice] = useState("");
  const [estimatedHours, setEstimatedHours] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchRequest = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiHandle.get(`/vendor-quote-request/${id}`);
        if (cancelled) return;
        if (res.data?.status) {
          setQuoteRequest(res.data.quote_request);
          setAlreadyQuoted(!!res.data.already_quoted);
          setMaxReached(!!res.data.max_reached);
          setQuoteCount(res.data.quote_count || 0);
        } else {
          setError(res.data?.message || "Failed to load quote request");
        }
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.message || "Failed to load quote request");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRequest();
    return () => { cancelled = true; };
  }, [id]);

  const _submit = async (e) => {
    e?.preventDefault?.();
    if (!price || Number(price) <= 0) {
      toast.error("Enter a valid price");
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiHandle.post(`/submit-quote/${id}`, {
        price: Number(price),
        estimated_hours: estimatedHours ? Number(estimatedHours) : null,
        message: message || null,
      });
      if (res.data?.status) {
        toast.success(res.data?.message || "Quote submitted");
        navigate("/dashboard/job-req");
      } else {
        toast.error(res.data?.message || "Failed to submit");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
        <CircularProgress sx={{ color: themeOrange }} />
      </Stack>
    );
  }

  if (error || !quoteRequest) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Quote request not available</Typography>
          <Typography variant="body2" color="text.secondary">{error || "Not found."}</Typography>
          <Button variant="outlined" sx={{ mt: 3 }} onClick={() => navigate("/dashboard/job-req")}>
            Back to Job Requests
          </Button>
        </Box>
      </Container>
    );
  }

  const blocked = alreadyQuoted || maxReached;
  const dates = Array.isArray(quoteRequest.date) ? quoteRequest.date : [];

  return (
    <Container maxWidth="md" sx={{ pb: 6 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Quote Request</Typography>
        <Chip
          label={`${quoteCount}/3 quotes received`}
          color={maxReached ? "default" : "primary"}
          size="small"
        />
      </Stack>

      <Box sx={{ p: 3, borderRadius: 2, bgcolor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", mb: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MdOutlineHandyman size={18} color={themeOrange} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {quoteRequest.service?.name || "Service"}
            </Typography>
          </Stack>
          {quoteRequest.location && (
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <FaLocationDot size={14} color="#666" style={{ marginTop: 4 }} />
              <Typography variant="body2">{quoteRequest.location}</Typography>
            </Stack>
          )}
          {dates.length > 0 && (
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <FaCalendarDays size={14} color="#666" style={{ marginTop: 4 }} />
              <Typography variant="body2">
                {dates.map((d) => d?.selected_date).filter(Boolean).join(", ") || "Not specified"}
              </Typography>
            </Stack>
          )}
          {quoteRequest.time && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <FaClock size={14} color="#666" />
              <Typography variant="body2">{quoteRequest.time}</Typography>
            </Stack>
          )}
          {quoteRequest.description && (
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <MdDescription size={16} color="#666" style={{ marginTop: 2 }} />
              <Typography variant="body2">{quoteRequest.description}</Typography>
            </Stack>
          )}
        </Stack>
      </Box>

      {blocked ? (
        <Box sx={{ p: 3, borderRadius: 2, bgcolor: "#fff7f0", border: "1px solid #ffd8b8" }}>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
            {alreadyQuoted
              ? "You've already submitted a quote for this request."
              : "This request already has 3 quotes."}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {alreadyQuoted
              ? "The customer will review all quotes and get back to you."
              : "New quotes aren't being accepted for this request."}
          </Typography>
          <Button variant="outlined" sx={{ mt: 2 }} onClick={() => navigate("/dashboard/job-req")}>
            Back to Job Requests
          </Button>
        </Box>
      ) : (
        <Box sx={{ p: 3, borderRadius: 2, bgcolor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Submit Your Quote</Typography>
          <Divider sx={{ mb: 3 }} />
          <form onSubmit={_submit}>
            <Stack spacing={2}>
              <TextField
                label="Price (CAD)"
                type="number"
                required
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                inputProps={{ min: 1, step: "0.01" }}
                fullWidth
              />
              <TextField
                label="Estimated hours (optional)"
                type="number"
                value={estimatedHours}
                onChange={(e) => setEstimatedHours(e.target.value)}
                inputProps={{ min: 0, step: "0.5" }}
                fullWidth
              />
              <TextField
                label="Message to customer (optional)"
                multiline
                minRows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                fullWidth
              />
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                sx={{ bgcolor: themeOrange, "&:hover": { bgcolor: "#d96429" }, py: 1.25 }}
              >
                {submitting ? "Submitting…" : "Submit Quote"}
              </Button>
            </Stack>
          </form>
        </Box>
      )}
    </Container>
  );
};

export default QuoteRequestDetail;
